import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button-variants";
import { Input } from "@/components/ui/input";
import { useApp } from "@/hooks/use-app-context";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import type { SettingsState } from "./use-settings";

/**
 * Account deletion, confirmed with the current password. A wrong password
 * shows under the field and keeps the dialog open; any other failure is a
 * toast. Once deleted, the session ends and the person lands on the login.
 */
export function DeleteAccountDialog({ state }: { state: SettingsState }) {
  const { t } = useApp();
  const { deletion } = state;

  return (
    <AlertDialog open={deletion.open} onOpenChange={deletion.setOpen}>
      <AlertDialogContent className="max-w-md gap-5 rounded-xl border-border/60 bg-background p-6 dark:bg-background/95">
        <AlertDialogHeader className="flex-row items-start gap-4 space-y-0 text-left">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-danger/10 text-danger">
            <Icon name="alertTriangle" className="size-5" />
          </div>
          <div className="space-y-1.5">
            <AlertDialogTitle className="text-base font-semibold">{t.deleteAccountTitle}</AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-muted-foreground">
              {t.deleteAccountDialogDesc}
              <span className="mt-2 block font-medium text-danger">{t.understandConsequences}</span>
            </AlertDialogDescription>
          </div>
        </AlertDialogHeader>

        <div className="space-y-2">
          <label htmlFor="delete-account-password" className="text-sm font-medium text-foreground">
            {t.settingsConfirmWithPassword}
          </label>
          <Input
            id="delete-account-password"
            type="password"
            autoComplete="current-password"
            value={deletion.password}
            onChange={(event) => deletion.setPassword(event.target.value)}
            aria-invalid={deletion.passwordError ? true : undefined}
            aria-describedby={deletion.passwordError ? "delete-account-password-error" : undefined}
            disabled={state.isDeleting}
          />
          {deletion.passwordError ? (
            <p id="delete-account-password-error" role="alert" className="text-sm text-danger">
              {deletion.passwordError}
            </p>
          ) : null}
        </div>

        <AlertDialogFooter className="gap-2 sm:gap-2">
          <AlertDialogCancel className={cn(buttonVariants({ variant: "outline", size: "sm" }), "mt-0")} disabled={state.isDeleting}>
            {t.cancelDeletion}
          </AlertDialogCancel>
          <AlertDialogAction
            className={cn(buttonVariants({ variant: "destructive", size: "sm" }))}
            disabled={state.isDeleting || !deletion.password}
            onClick={(event) => {
              // Stay open on a wrong password instead of closing the dialog.
              event.preventDefault();
              void deletion.confirm();
            }}
          >
            {state.isDeleting ? <Icon name="loader2" className="size-4 animate-spin" /> : null}
            {state.isDeleting ? t.deleting : t.confirmDeletion}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
